import React, { useEffect, useState } from "react";
import { Col, Divider, Row, Select } from "antd";
import { Controller, useFieldArray, useForm } from "react-hook-form";
import styles from "./add-edit-classes.module.css";
import CustomDrawer from "../../../../../../../../../common/custom-drawer/custom-drawer";
import CustomTextfield from "../../../../../../../../../common/input/custom-textfield/custom-textfield";
import CustomButton from "../../../../../../../../../common/custom-button/custom-button";
import { toast } from "react-toastify";
import { fetchClass } from "../../../../../../../../../services/student";
import { PlusOutlined } from "@ant-design/icons";
import SVGIcon from "../../../../../../../../../constants/SVGIcon";
import CustomSelect from "../../../../../../../../../common/input/custom-select/custom-select";
import { COLOR_CODE, generateTimeSlots } from "../../../../../../../../../constants/common";
import { createClassroom, updateBulkClassroom } from "../../../../../../../../../services/class_routine";
import { createAntdSelection, refetchSchool } from "../../../../../../../../../utils/common";
import { fetchTeacherList } from "../../../../../../../../../services/teachers";

const { Option } = Select;

const AddEditClasses = ({ open, onClose, data }) => {
  const [loading, setLoading] = useState(false);
  const [classList, setClassList] = useState([]);
  const [teacherList, setTeacherList] = useState([]);
  const { control, getValues, reset, trigger } = useForm({
    mode: "onChange",
    defaultValues: {
      level: "",
      sectionSets: [
        {
          id: "",
          sectionName: "",
          sectionColor: "",
          studentCapacity: "",
          timing: "",
          classTeacher: "",
        },
      ],
    },
  });
  const { fields, append, remove } = useFieldArray({
    control,
    name: "sectionSets",
  });

  useEffect(() => {
    if (!open) return;
    getClassInfo();
  }, [open]);

  useEffect(() => {
    if (data?.id) {
      reset({
        level: data?.id,
        sectionSets: data?.classRooms?.length > 0
          ? data.classRooms?.map(item => ({
              id: item?.id,
              sectionName: item?.name,
              sectionColor: item?.colour_code,
              studentCapacity: item?.capacity,
              timing: item?.metadata?.timing,
              classTeacher: item?.class_teacher_id,
            }))
          : [
              {
                id: "",
                sectionName: "",
                sectionColor: "",
                studentCapacity: "",
                timing: "",
                classTeacher: "",
              },
            ],
      });
    } else {
      reset({
        level: "",
        sectionSets: [
          {
            id: "",
            sectionName: "",
            sectionColor: "",
            studentCapacity: "",
            timing: "",
            classTeacher: "",
          },
        ],
      });
    }
  }, [data, reset]);

  const getClassInfo = async () => {
    try {
      setLoading(true);
      const [levels, teachers] = await Promise.all([
        fetchClass(),
        fetchTeacherList(),
      ]);

      setClassList(levels?.data?.data?.levels);
      setTeacherList(createAntdSelection(teachers?.data?.data, "full_name"));
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    try {
      const isValid = await trigger();
      if (!isValid) {
        return;
      }

      const formValues = getValues();
      const payload = {
        level_id: formValues?.level,
        classrooms: formValues?.sectionSets?.map(item => ({
          id: item?.id,
          name: item?.sectionName,
          colour_code: item?.sectionColor,
          capacity: item?.studentCapacity,
          metadata: { timing: item?.timing },
          class_teacher_id: item?.classTeacher,
        }))
      };

      if (data?.id) await updateBulkClassroom(payload);
      else await createClassroom(payload);
      toast.success(`Class ${data?.id ? "edited" : "created"} successfully!`);
      refetchSchool();
      handleClose();
    } catch (err) {
      console.log(err);
      toast.error(err?.data?.message || `Failed to ${data?.id ? "edit" : "create"} class`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <CustomDrawer
      title={`${data?.id ? 'Edit' : 'Add New'} Class`}
      open={open}
      onClose={handleClose}
      className={styles.drawer}
      footer={
        <div className={styles.footerWrapper}>
          <CustomButton
            onClick={handleClose}
            title="Back"
            variant="outlined"
            className={styles.footerBackButton}
          />
          <CustomButton
            title={`${data?.id ? "Edit" : "Add New"} Class`}
            size="large"
            onClick={handleSubmit}
          />
        </div>
      }
      width="40vw"
      drawerStyles={{ body: { padding: "50px" } }}
    >
      <Row gutter={[0, 20]}>
        <Col span={16}>
          <Controller
            name="level"
            control={control}
            rules={{ required: true }}
            render={({
              field: { onChange, value },
              fieldState: { error },
            }) => (
              <CustomSelect
                id="level"
                label="Level"
                showRequired
                labelRowor="#171717"
                placeholder="--Select--"
                showSearch
                size="large"
                value={value}
                onChange={onChange}
                status={error ? "error" : ""}
              >
                {classList?.map(item => (
                  <Select.Option key={item?.id} value={item?.id}>
                    {item?.name}
                  </Select.Option>
                ))}
              </CustomSelect>
            )}
          />
        </Col>
        <Col span={8} className={styles.addClassWrapper}>
          <CustomButton
            title="Add Class"
            type="text"
            icon={
              <div className={styles.iconCircle}>
                <PlusOutlined />
              </div>
            }
            className={styles.addClassBtn}
            showTooltip={false}
            onClick={() => append({ id: "", sectionName: "", sectionColor: "", studentCapacity: "", timing: "" })}
          />
        </Col>
      </Row>

      <Divider style={{ backgroundColor: "#d9d9d9" }} />

      {fields?.map((field, index) => (
        <Row gutter={[10, 20]} className={styles.classRoomWrapper} key={field?.id}>
          {fields?.length > 1 && (
            <Col span={24} className={styles.deleteClassIconWrapper}>
              <SVGIcon
                name={"delete"}
                width={"20"}
                height={"20"}
                className={styles.deleteClassIcon}
                stroke={"rgba(243, 17, 17, 1)"}
                onClick={() => remove(index)}
              />
            </Col>
          )}

          <Col span={12}>
            <Controller
              name={`sectionSets.${index}.sectionName`}
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id={`sectionSets.${index}.sectionName`}
                  label="Class Name"
                  showRequired
                  labelColor="#171717"
                  showSearch
                  size="large"
                  value={value}
                  placeholder="Enter Class Name"
                  onChange={onChange}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name={`sectionSets.${index}.sectionColor`}
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomSelect
                  id={`sectionSets.${index}.sectionColor`}
                  label="Select Class Color"
                  showRequired
                  labelColor="#171717"
                  placeholder="--Select--"
                  showSearch
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                >
                  {COLOR_CODE?.map((hex) => (
                    <Option key={hex} value={hex}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <div
                          style={{
                            height: 20,
                            width: 20,
                            borderRadius: 2,
                            backgroundColor: `${hex}1A`,
                            border: `1px solid ${hex}66`,
                          }}
                        />
                        {hex}
                      </div>
                    </Option>
                  ))}
                </CustomSelect>
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name={`sectionSets.${index}.timing`}
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomSelect
                  id={`sectionSets.${index}.timing`}
                  label="Timing"
                  labelColor="#171717"
                  placeholder="--Select--"
                  showSearch
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                >
                  {generateTimeSlots().map((option) => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </CustomSelect>
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name={`sectionSets.${index}.studentCapacity`}
              control={control}
              rules={{ required: true }}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomTextfield
                  id={`sectionSets.${index}.studentCapacity`}
                  label="Student Capacity"
                  showRequired
                  labelColor="#171717"
                  showSearch
                  size="large"
                  type="number"
                  value={value}
                  placeholder="Enter Student Capacity"
                  onChange={onChange}
                  status={error ? "error" : ""}
                />
              )}
            />
          </Col>

          <Col span={12}>
            <Controller
              name={`sectionSets.${index}.classTeacher`}
              control={control}
              render={({
                field: { onChange, value },
                fieldState: { error },
              }) => (
                <CustomSelect
                  id={`sectionSets.${index}.classTeacher`}
                  label="Class Teacher"
                  labelColor="#171717"
                  placeholder="--Select--"
                  showSearch
                  size="large"
                  value={value}
                  onChange={onChange}
                  status={error ? "error" : ""}
                >
                  {teacherList?.map((teacher) => (
                    <Option key={teacher?.value} value={teacher?.value}>
                      {teacher?.label}
                    </Option>
                  ))}
                </CustomSelect>
              )}
            />
          </Col>
        </Row>
      ))}
    </CustomDrawer>
  );
};

export default AddEditClasses;
